import { useEffect, useState } from 'react'
import { useTrackingContext } from 'shared/libraries/frontend-shared/src/context/TrackingContext'
import useLocalStorage from 'shared/libraries/frontend-shared/src/hooks/useLocalStorage'
import {
  EventAction,
  EventName,
  TrackingErrorType,
  TrackingEventType,
} from 'shared/libraries/frontend-shared/src/models/Tracking'
import CheckoutUtils from 'shared/libraries/frontend-shared/src/utils/checkout.utils'
import { ConsentCheckboxData } from 'shared/libraries/gcdm-token/src/model'
import { CheckoutSection, Customer, LocalizedMarket } from 'shared/model'
import { Vehicle } from 'shared/model/vehicle'

import { useAppContext } from '@/contexts/AppContext'
import { useSelectedServices } from '@/contexts/SelectedServicesContext'
import { AppointmentResponse } from '@/models/appointment'
import { BookingEngine } from '@/models/bookingengine'
import { LocalStorageKeys } from '@/models/localstorage'
import GuestServicesResponse from '@/models/services'
import Dealer from '@/models/SrpDealer'
import Timeslots, { SelectedTimeslot } from '@/models/timeslots'
import { getAvailableTimeslots, isTimeslotsValid } from '@/utils/timeslot-utils'
import { getDynamicTrackingData } from '@/utils/tracking.utils'

interface Props {
  page: 'index' | 'timeslot' | 'checkout' | 'confirmation'
  customer: Customer
  marketConfiguration: LocalizedMarket
  indexPage?: {
    getServicesError?: Error
    getDealerError?: Error
    getBookingEngineError?: Error
    servicesResponse?: GuestServicesResponse
    bookingEngine?: BookingEngine
  }
  timeslotPage?: {
    isValidatingTimeslot: boolean
    getTimeslotsError: Error
    timeslots: Timeslots
    receivedTimeslots: Timeslots
    selectedDate: string
    selectedTimeslot: SelectedTimeslot
    isCalendarShown: boolean
  }
  checkoutPage?: {
    isSubmitAppointmentEnabled: boolean
    currentEditSection: CheckoutSection
    submitAppointmentError: Error
    isSubmittingAppointment: boolean
    isAddressSaved: boolean
    isPersonalDataSaved: boolean
    hasAcceptedDataRelay: boolean
  }
  confirmationPage?: {
    appointmentData: AppointmentResponse
    userConsents: ConsentCheckboxData[]
  }
  stage: string
}

export const useTracking = ({
  page,
  marketConfiguration,
  indexPage = null,
  timeslotPage = null,
  checkoutPage = null,
  confirmationPage = null,
  customer = null,
  stage,
}): Props => {
  const { selectedServices } = useSelectedServices()
  const [isPageViewFired, setIsPageViewFired] = useState(false)
  const [vehicle] = useLocalStorage<Vehicle>(LocalStorageKeys.VEHICLE, null)
  const [dealer] = useLocalStorage<Dealer>(LocalStorageKeys.SELECTED_DEALER, null)
  const [firedEvents, setFiredEvents] = useState({
    dealerError: false,
    bookingEngineError: false,
    servicesError: false,
    servicesLoaded: false,
    servicesSelected: false,
    timeslotLoadError: false,
    dateTimeError: false,
    dateSelected: false,
    selectedTimeslot: false,
    submitAppointmentError: false,
    isSubmittingAppointment: false,
    confirmation: false,
    checkoutSuccess: false,
    hasAcceptedDataRelay: false,
    isAddressSaved: false,
    isPersonalDataSaved: false,
  })

  const { trackEvent, trackPageLoadEvent } = useTrackingContext()
  const { isEpaasLoaded } = useAppContext()
  const locale = marketConfiguration?.localizedConfig?.locale
  const brand = marketConfiguration?.brand
  const optinVersion = marketConfiguration?.optinVersion

  useEffect(() => {
    if (isEpaasLoaded && !isPageViewFired) {
      switch (page) {
        case 'index': {
          const dynamicTrackingData = getDynamicTrackingData({
            pageVariant: 'real page',
            pageSubCategory1: 'form start intention',
            pageID: 'sales > oneshop > form > SRP entry',
            pageName: 'sales > oneshop > SRP entry',
            locale,
            brand,
            dealer,
            customer,
            vehicle,
            services: selectedServices,
            stage,
          })
          setIsPageViewFired(true)
          trackPageLoadEvent({
            trackingData: dynamicTrackingData,
          })
          break
        }

        case 'timeslot': {
          setIsPageViewFired(true)
          if (!selectedDate && !selectedTimeslot) {
            const dynamicTrackingData = getDynamicTrackingData({
              pageVariant: 'virtual page',
              pageSubCategory1: `step ${2}/7`,
              pageID: 'SRP date: entry = form step 2',
              pageName: 'sales > oneshop > SRP date: entry',
              locale,
              brand,
              dealer,
              customer,
              vehicle,
              services: selectedServices,
              stage,
            })
            trackPageLoadEvent({
              trackingData: dynamicTrackingData,
            })
            trackEvent({
              dynamicTrackingData,
              eventEffect: 'sales > form step 2',
              subCategory: 'oneshop service repair package form',
              subCategory2: 'handraiser',
              eventCause: 'default',
              eventAction: EventAction.INTERNAL_CLICK,
              eventName: EventName.FORM,
              eventPoints: '200',
              eventPrimaryCategory: 'custom central form',
              eventType: TrackingEventType.RFS,
            })
          }
          break
        }
        default:
          break
      }
    }
  }, [isEpaasLoaded, page, indexPage?.bookingEngine])

  /* --------------- SRP Home page ------------------ */
  const { getServicesError, getDealerError, getBookingEngineError, servicesResponse, bookingEngine } = indexPage || {}

  useEffect(() => {
    if (vehicle && bookingEngine) {
      trackEvent({
        dynamicTrackingData: getDynamicTrackingData({
          pageVariant: 'real page',
          pageSubCategory1: 'form start intention',
          pageID: 'sales > oneshop > form > SRP entry',
          pageName: 'sales > oneshop > SRP entry',
          locale,
          brand,
          dealer,
          customer,
          vehicle,
          services: selectedServices,
          stage,
        }),
        subCategory: 'oneshop service repair package form',
        subCategory2: 'handraiser',
        eventCause: 'default',
        eventEffect: 'sales > form start intention',
        eventAction: EventAction.INTERNAL_CLICK,
        eventName: EventName.FORM,
        eventPoints: '200',
        eventPrimaryCategory: 'custom central form',
        eventType: TrackingEventType.RFS,
      })
    }
  }, [bookingEngine])

  useEffect(() => {
    if (page === 'index') {
      if (!getServicesError && servicesResponse && servicesResponse?.services?.length) {
        const dynamicTrackingData = getDynamicTrackingData({
          pageVariant: 'virtual page',
          pageSubCategory1: `step ${1}/7`,
          pageID: 'sales > oneshop > form > SRP entry: service selection',
          pageName: 'sales > oneshop > SRP entry: service selection',
          locale,
          brand,
          dealer,
          customer,
          vehicle,
          services: selectedServices,
          stage,
        })
        trackPageLoadEvent({ trackingData: dynamicTrackingData })
        if (!firedEvents.servicesLoaded) {
          setFiredEvents({ ...firedEvents, servicesLoaded: true })
          trackEvent({
            dynamicTrackingData,
            subCategory: 'oneshop service repair package form',
            subCategory2: 'handraiser',
            eventCause: 'default',
            eventEffect: 'sales > form started',
            eventAction: EventAction.FIRST_PAGE_LOAD,
            eventName: EventName.FORM,
            eventPoints: '200',
            eventPrimaryCategory: 'custom central form',
            eventType: TrackingEventType.RFS,
          })
        }
      }
    }
  }, [servicesResponse])

  useEffect(() => {
    if (selectedServices?.length && !firedEvents.servicesSelected && page === 'index') {
      const dynamicTrackingData = getDynamicTrackingData({
        pageVariant: 'virtual page',
        pageSubCategory1: `step ${1}/7`,
        pageID: 'sales > oneshop > form > SRP entry: service selection',
        pageName: 'sales > oneshop > SRP entry: service selection',
        locale,
        brand,
        dealer,
        customer,
        vehicle,
        services: selectedServices,
        stage,
      })
      setFiredEvents({ ...firedEvents, servicesSelected: true })
      trackEvent({
        dynamicTrackingData,
        subCategory: 'oneshop service repair package form',
        subCategory2: 'handraiser',
        eventCause: 'default',
        eventEffect: 'sales > first form interaction',
        eventAction: EventAction.CHECKBOX,
        eventName: EventName.FORM,
        eventPoints: '200',
        eventPrimaryCategory: 'custom central form',
        eventType: TrackingEventType.RFS,
      })
    }
  }, [selectedServices?.length])

  useEffect(() => {
    const dynamicTrackingData = getDynamicTrackingData({
      pageVariant: 'real page',
      pageSubCategory1: 'form start intention',
      pageID: 'sales > oneshop > form > SRP entry',
      pageName: 'sales > oneshop > SRP entry',
      locale,
      brand,
      dealer,
      customer,
      vehicle,
      services: selectedServices,
      stage,
    })

    if (getDealerError && !firedEvents.dealerError) {
      setFiredEvents({ ...firedEvents, dealerError: true })
      trackEvent({
        dynamicTrackingData,
        eventPrimaryCategory: 'custom central form',
        eventEffect: 'call to dealer endpoint failed',
        eventCause: JSON.stringify(getDealerError?.message),
        eventAction: EventAction.LOAD_DEALER_FAILED,
        eventName: EventName.ERROR,
        eventPoints: '200',
        eventType: TrackingErrorType.BACKEND_FIRST_PARTY,
      })
    }
    if (getBookingEngineError && !firedEvents.bookingEngineError) {
      setFiredEvents({ ...firedEvents, bookingEngineError: true })
      trackEvent({
        dynamicTrackingData,
        eventPrimaryCategory: 'custom central form',
        eventEffect: 'call to booking engine endpoint failed',
        eventCause: JSON.stringify(getBookingEngineError?.message),
        eventAction: EventAction.LOAD_BOOKING_ENGINE_LOAD,
        eventName: EventName.ERROR,
        eventPoints: '200',
        eventType: TrackingErrorType.BACKEND_FIRST_PARTY,
      })
    }
    if (getServicesError && !firedEvents.servicesError) {
      setFiredEvents({ ...firedEvents, servicesError: true })
      trackEvent({
        dynamicTrackingData,
        eventPrimaryCategory: 'custom central form',
        eventEffect: 'call to services endpoint failed',
        eventCause: JSON.stringify(getBookingEngineError?.message),
        eventAction: EventAction.LOAD_SERVICES_FAILED,
        eventName: EventName.ERROR,
        eventPoints: '200',
        eventType: TrackingErrorType.BACKEND_FIRST_PARTY,
      })
    }
  }, [getDealerError, getBookingEngineError, getServicesError])
  /* --------------- SRP Home page ------------------ */

  /* --------------- SRP Timeslot page ------------------ */
  const {
    isValidatingTimeslot,
    getTimeslotsError,
    timeslots,
    selectedDate,
    receivedTimeslots,
    selectedTimeslot,
    isCalendarShown,
  } = timeslotPage || {}
  const [formStep, setFormStep] = useState(null)

  useEffect(() => {
    const dynamicTrackingData = getDynamicTrackingData({
      pageVariant: 'virtual page',
      pageSubCategory1: `step ${3}/8`,
      pageID: 'SRP date: date = form step 3',
      pageName: 'sales > oneshop > SRP date: date',
      locale,
      brand,
      dealer,
      customer,
      vehicle,
      services: selectedServices,
      stage,
    })
    if (!firedEvents.dateSelected && selectedDate) {
      setFiredEvents({ ...firedEvents, dateSelected: true })
      trackEvent({
        dynamicTrackingData,
        subCategory: 'oneshop service repair package form',
        subCategory2: 'handraiser',
        eventCause: 'default',
        eventEffect: 'sales > form step 3',
        eventAction: EventAction.INTERNAL_CLICK,
        eventName: EventName.FORM,
        eventPoints: '200',
        eventPrimaryCategory: 'custom central form',
        eventType: TrackingEventType.RFS,
      })
    }
  }, [selectedDate])

  useEffect(() => {
    const dynamicTrackingData = getDynamicTrackingData({
      pageVariant: 'virtual page',
      pageSubCategory1: `step ${3}/7`,
      pageID: 'SRP date: date = form step 3',
      pageName: 'sales > oneshop > SRP date: date',
      locale,
      brand,
      dealer,
      customer,
      vehicle,
      services: selectedServices,
      stage,
    })
    if (!isValidatingTimeslot && !getTimeslotsError && isTimeslotsValid(timeslots) && isCalendarShown) {
      trackPageLoadEvent({ trackingData: dynamicTrackingData })
      trackEvent({
        dynamicTrackingData,
        eventEffect: 'sales > form step 3',
        subCategory: 'oneshop service repair package form',
        subCategory2: 'handraiser',
        eventCause: 'default',
        eventAction: EventAction.INTERNAL_CLICK,
        eventName: EventName.FORM,
        eventPoints: '200',
        eventPrimaryCategory: 'custom central form',
        eventType: TrackingEventType.RFS,
      })
    }
    if (getTimeslotsError && !firedEvents.timeslotLoadError) {
      setFiredEvents({ ...firedEvents, timeslotLoadError: true })
      trackEvent({
        dynamicTrackingData,
        eventPrimaryCategory: 'custom central form',
        eventEffect: 'call to timeslots endpoint failed',
        eventCause: JSON.stringify(getTimeslotsError?.message),
        eventAction: EventAction.LOAD_TIMESLOTS_FAILED,
        eventName: EventName.ERROR,
        eventPoints: '200',
        eventType: TrackingErrorType.BACKEND_FIRST_PARTY,
      })
    }
  }, [isValidatingTimeslot, getTimeslotsError, timeslots, isCalendarShown])

  useEffect(() => {
    const availableTimeslots = getAvailableTimeslots(receivedTimeslots, selectedDate)
    const dynamicTrackingData = getDynamicTrackingData({
      pageVariant: 'virtual page',
      pageSubCategory1: `step ${4}/7`,
      pageID: 'SRP date: time = form step 4',
      pageName: 'sales > oneshop > SRP date: time',
      locale,
      brand,
      dealer,
      customer,
      vehicle,
      services: selectedServices,
      stage,
    })
    if (selectedDate && !isValidatingTimeslot && availableTimeslots && formStep !== 4) {
      setFormStep(4)
      trackPageLoadEvent({ trackingData: dynamicTrackingData })
    }
    if (!availableTimeslots && selectedDate && !firedEvents.dateTimeError) {
      setFiredEvents({ ...firedEvents, dateTimeError: true })
      trackEvent({
        dynamicTrackingData,
        eventPrimaryCategory: 'custom central form',
        eventEffect: 'getting available timeslots for selected date failed',
        eventCause: JSON.stringify({
          receivedTimeslots: receivedTimeslots?.timeslotOptions,
          selectedDate,
        }),
        eventAction: EventAction.LOAD_TIMESLOT_FOR_DATE_FAILED,
        eventName: EventName.ERROR,
        eventPoints: '200',
        eventType: TrackingErrorType.FRONTEND_COMPONENT,
      })
    }
  }, [receivedTimeslots?.timeslotOptions?.length, isValidatingTimeslot, selectedDate])

  useEffect(() => {
    if (selectedTimeslot?.start) {
      if (!firedEvents.selectedTimeslot) {
        const dynamicTrackingData = getDynamicTrackingData({
          pageVariant: 'virtual page',
          pageSubCategory1: `step ${4}/7`,
          pageID: 'SRP date: time = form step 4',
          pageName: 'sales > oneshop > SRP date: time',
          locale,
          brand,
          dealer,
          customer,
          vehicle,
          services: selectedServices,
          stage,
        })
        setFiredEvents({ ...firedEvents, selectedTimeslot: true })
        trackEvent({
          dynamicTrackingData,
          subCategory: 'oneshop service repair package form',
          subCategory2: 'handraiser',
          eventCause: 'default',
          eventEffect: 'sales > form step 4',
          eventAction: EventAction.INTERNAL_CLICK,
          eventName: EventName.FORM,
          eventPoints: '200',
          eventPrimaryCategory: 'custom central form',
          eventType: TrackingEventType.RFS,
        })
      }
    }
  }, [selectedTimeslot])

  /* --------------- SRP Timeslot page ------------------ */

  /* --------------- SRP Checkout page ------------------ */
  const {
    isSubmitAppointmentEnabled,
    currentEditSection,
    submitAppointmentError,
    isSubmittingAppointment,
    isAddressSaved,
    isPersonalDataSaved,
    hasAcceptedDataRelay,
  } = checkoutPage || {}

  const getCheckoutStepPageInfo = (currentEditSection: CheckoutSection) => {
    switch (currentEditSection) {
      case CheckoutSection.PERSONAL_DATA:
        return {
          pageSubCategory1: `step ${5}/7`,
          pageID: 'SRP checkout: personal detail = form step 5',
          pageName: 'sales > oneshop > SRP checkout: personal detail',
        }
      case CheckoutSection.ADDRESS_DATA:
        return {
          pageSubCategory1: `step ${6}/7`,
          pageID: 'SRP checkout: address detail = form step 6',
          pageName: 'sales > oneshop > SRP checkout: address detail',
        }
      default:
        return {
          pageSubCategory1: `step ${7}/7`,
          pageID: 'SRP checkout: summary = form step 7',
          pageName: 'sales > oneshop > SRP checkout: summary',
        }
    }
  }

  useEffect(() => {
    if (isSubmitAppointmentEnabled && isEpaasLoaded) {
      const dynamicTrackingData = getDynamicTrackingData({
        pageVariant: 'virtual page',
        ...getCheckoutStepPageInfo(currentEditSection),
        locale,
        brand,
        dealer,
        customer,
        vehicle,
        services: selectedServices,
        stage,
      })
      trackPageLoadEvent({ trackingData: dynamicTrackingData })
    }
  }, [isSubmitAppointmentEnabled, currentEditSection, isEpaasLoaded])

  useEffect(() => {
    if (isPersonalDataSaved && !firedEvents.isPersonalDataSaved) {
      setFiredEvents({ ...firedEvents, isPersonalDataSaved: true })
      trackEvent({
        dynamicTrackingData: getDynamicTrackingData({
          pageVariant: 'virtual page',
          ...getCheckoutStepPageInfo(CheckoutSection.PERSONAL_DATA),
          locale,
          brand,
          dealer,
          customer,
          vehicle,
          services: selectedServices,
          stage,
        }),
        subCategory: 'oneshop service repair package form',
        subCategory2: 'handraiser',
        eventCause: 'default',
        eventEffect: 'sales > form step 5',
        eventAction: EventAction.INTERNAL_CLICK,
        eventName: EventName.FORM,
        eventPoints: '200',
        eventPrimaryCategory: 'custom central form',
        eventType: TrackingEventType.RFS,
      })
    }

    if (isAddressSaved && !firedEvents.isAddressSaved) {
      setFiredEvents({ ...firedEvents, isAddressSaved: true })
      trackEvent({
        dynamicTrackingData: getDynamicTrackingData({
          pageVariant: 'virtual page',
          ...getCheckoutStepPageInfo(CheckoutSection.ADDRESS_DATA),
          locale,
          brand,
          dealer,
          customer,
          vehicle,
          services: selectedServices,
          stage,
        }),
        subCategory: 'oneshop service repair package form',
        subCategory2: 'handraiser',
        eventCause: 'default',
        eventEffect: 'sales > form step 6',
        eventAction: EventAction.INTERNAL_CLICK,
        eventName: EventName.FORM,
        eventPoints: '200',
        eventPrimaryCategory: 'custom central form',
        eventType: TrackingEventType.RFS,
      })
    }

    if (hasAcceptedDataRelay && !firedEvents.hasAcceptedDataRelay) {
      setFiredEvents({ ...firedEvents, hasAcceptedDataRelay: true })
      trackEvent({
        dynamicTrackingData: getDynamicTrackingData({
          pageVariant: 'virtual page',
          ...getCheckoutStepPageInfo(currentEditSection),
          locale,
          brand,
          dealer,
          customer,
          vehicle,
          services: selectedServices,
          stage,
        }),
        subCategory: 'oneshop service repair package form',
        subCategory2: 'handraiser',
        eventCause: 'default',
        eventEffect: 'sales > form step 7',
        eventAction: EventAction.INTERNAL_CLICK,
        eventName: EventName.FORM,
        eventPoints: '200',
        eventPrimaryCategory: 'custom central form',
        eventType: TrackingEventType.RFS,
      })
    }
  }, [isAddressSaved, isPersonalDataSaved, hasAcceptedDataRelay])

  useEffect(() => {
    const dynamicTrackingData = getDynamicTrackingData({
      pageVariant: 'virtual page',
      pageSubCategory1: `step ${7}/7`,
      pageID: 'SRP checkout: = form finished intention',
      pageName: 'sales > oneshop > SRP checkout: form finished intention',
      locale,
      brand,
      dealer,
      customer,
      vehicle,
      services: selectedServices,
      stage,
    })

    if (submitAppointmentError && !firedEvents.submitAppointmentError) {
      setFiredEvents({ ...firedEvents, submitAppointmentError: true })
      trackEvent({
        dynamicTrackingData,
        eventPrimaryCategory: 'custom central form',
        eventEffect: 'submitting customer appointment has failed',
        eventCause: JSON.stringify(getTimeslotsError?.message),
        eventAction: EventAction.SUBMIT_APPOINTMENT_FAILED,
        eventName: EventName.ERROR,
        eventPoints: '200',
        eventType: TrackingErrorType.BACKEND_FIRST_PARTY,
      })
    }
    if (isSubmittingAppointment && !firedEvents.isSubmittingAppointment) {
      setFiredEvents({ ...firedEvents, isSubmittingAppointment: true })
      trackEvent({
        dynamicTrackingData,
        subCategory: 'oneshop service repair package form',
        subCategory2: 'handraiser',
        eventCause: 'default',
        eventEffect: 'sales > form finished intention',
        eventAction: EventAction.INTERNAL_CLICK,
        eventName: EventName.FORM,
        eventPoints: '200',
        eventPrimaryCategory: 'custom central form',
        eventType: TrackingEventType.RFS,
      })
    }
  }, [submitAppointmentError, isSubmittingAppointment])
  /* --------------- SRP Checkout page ------------------ */

  /* --------------- SRP Confirmation page ------------------ */
  const { appointmentData, userConsents } = confirmationPage || {}
  useEffect(() => {
    let isConsentRender = false
    isConsentRender = userConsents ? CheckoutUtils.isConsentRendered(userConsents, optinVersion) : undefined

    const dynamicTrackingData = getDynamicTrackingData({
      pageVariant: 'real page',
      pageSubCategory1: ``,
      pageSubCategory2: 'dedicated form',
      pageSubCategory3: !isConsentRender
        ? 'no consent shown'
        : optinVersion === 'A'
        ? 'consent version A shown'
        : 'consent version B shown',
      pageID: 'sales > oneshop > form > SRP checkout: success',
      pageName: 'sales > oneshop > SRP checkout: success',
      locale,
      brand,
      dealer,
      customer,
      vehicle,
      services: selectedServices,
      stage,
    })
    if (appointmentData) {
      trackPageLoadEvent({ trackingData: dynamicTrackingData })
    }

    if (appointmentData && isEpaasLoaded && !firedEvents.confirmation) {
      setFiredEvents({ ...firedEvents, confirmation: true })
      trackEvent({
        dynamicTrackingData,
        subCategory: 'oneshop service repair package form',
        subCategory2: 'handraiser',
        eventCause: 'default',
        eventEffect: 'sales > form finished',
        eventAction: EventAction.PAGE_LOAD,
        eventName: EventName.FORM,
        eventPoints: '200',
        eventPrimaryCategory: 'custom central form',
        eventType: TrackingEventType.RFS,
      })
    }
  }, [appointmentData])
  /* --------------- SRP Confirmation page ------------------ */
  return null
}
